import React from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { IAddProductInput, IAttribute } from "muminsmart-utils";
import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Dropzone } from "@/components/ui/dropzone";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import ProductAttributeField from "./ProductAttribute";
import { FieldGroup, FieldLabel, FieldSet } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";

interface VariantCardFieldsProps {
  form: UseFormReturn<IAddProductInput>;
  index: number;
  variantAttributes: Array<{ attribute: IAttribute; value: string }>;
  onGenerateSku: (index: number) => void;
  onAddBulkPrice: (index: number) => void;
  onDeleteBulkPrice: (index: number, bulkPriceIndex: number) => void;
}

const VariantCardFields: React.FC<VariantCardFieldsProps> = ({
  form,
  index,
  variantAttributes,
  onGenerateSku,
  onAddBulkPrice,
  onDeleteBulkPrice,
}) => {
  const bulkPrices =
    useWatch({
      control: form.control,
      name: `variants.${index}.bulkPrices`,
    }) ?? [];
  const alwaysAvailable =
    useWatch({
      control: form.control,
      name: `variants.${index}.alwaysAvailable`,
    }) ?? false;

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 lg:gap-3">
      {variantAttributes.map((variantAttribute, attributeIndex) => (
        <ProductAttributeField
          key={variantAttribute.attribute.id}
          attribute={variantAttribute.attribute}
          variantIndex={index}
          attributeIndex={attributeIndex}
        />
      ))}

      <FormField
        control={form.control}
        name={`variants.${index}.sku`}
        render={({ field }) => (
          <FormItem className="min-w-0">
            <FormLabel className="font-medium">Product Code (SKU)</FormLabel>
            <FormControl>
              <InputGroup className="h-auto rounded-3xl border-muted bg-white">
                <InputGroupInput
                  placeholder="Product Code (SKU)"
                  className="px-3 py-3"
                  {...field}
                />
                {!field.value?.trim() && (
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onGenerateSku(index)}
                      className="h-8 shrink-0 rounded-full bg-primary px-3 text-xs font-medium text-primary-foreground shadow-none transition-colors hover:bg-primary/90"
                    >
                      Generate SKU
                    </InputGroupButton>
                  </InputGroupAddon>
                )}
              </InputGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`variants.${index}.weight`}
        render={({ field }) => (
          <FormItem className="min-w-0">
            <FormLabel className="font-medium">Weight (kgs)</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="Weight (kgs)"
                className="w-full rounded-3xl border-muted bg-white p-3"
                type="number"
                step="0.1"
                min="0"
                max="50"
                onChange={(event) =>
                  field.onChange(parseFloat(event.target.value))
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`variants.${index}.price`}
        render={({ field }) => (
          <FormItem className="min-w-0">
            <FormLabel className="font-medium">Price</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="Price"
                className="w-full rounded-3xl border-muted bg-white p-3"
                type="number"
                onChange={(event) =>
                  field.onChange(parseInt(event.target.value, 10))
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`variants.${index}.discountedPrice`}
        render={({ field }) => (
          <FormItem className="min-w-0">
            <FormLabel className="font-medium">Discounted Price</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="Discounted Price"
                className="w-full rounded-3xl border-muted bg-white p-3"
                type="number"
                onChange={(event) =>
                  field.onChange(parseInt(event.target.value, 10))
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`variants.${index}.stock`}
        render={({ field }) => (
          <FormItem className="min-w-0">
            <FormLabel className="font-medium">Current Stock</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="Current Stock"
                className="w-full rounded-3xl border-muted bg-white p-3"
                type="number"
                disabled={alwaysAvailable}
                onChange={(event) =>
                  field.onChange(parseInt(event.target.value, 10))
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`variants.${index}.alwaysAvailable`}
        render={({ field }) => (
          <FormItem className="flex min-w-0 flex-row items-center gap-3 space-y-0 rounded-3xl border border-muted bg-white p-3">
            <FormControl>
              <Checkbox
                checked={field.value ?? false}
                onCheckedChange={(checked) =>
                  field.onChange(checked === true)
                }
              />
            </FormControl>
            <FormLabel className="font-medium">Always Available</FormLabel>
          </FormItem>
        )}
      />

      <FieldGroup className="col-span-full mt-2 gap-2">
        <FieldSet>
          <FieldLabel>Bulk Prices</FieldLabel>
        </FieldSet>

        {bulkPrices.map((_, bulkPriceIndex) => (
          <div
            key={`variant-${index}-bulk-${bulkPriceIndex}`}
            className="rounded-3xl border border-muted bg-white p-3"
          >
            <div className="mb-2 flex w-full items-center justify-between gap-2 border-b pb-2 text-sm">
              <p className="font-medium">Bulk Price {bulkPriceIndex + 1}</p>
              <button
                type="button"
                onClick={() => onDeleteBulkPrice(index, bulkPriceIndex)}
                aria-label={`Remove bulk price ${bulkPriceIndex + 1}`}
                className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name={`variants.${index}.bulkPrices.${bulkPriceIndex}.quantity`}
                render={({ field }) => (
                  <FormItem className="min-w-0">
                    <FormLabel className="font-medium">Qty</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Qty"
                        className="w-full rounded-3xl border-muted bg-white p-3"
                        type="number"
                        {...field}
                        {...form.register(
                          `variants.${index}.bulkPrices.${bulkPriceIndex}.quantity`,
                          { valueAsNumber: true },
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`variants.${index}.bulkPrices.${bulkPriceIndex}.price`}
                render={({ field }) => (
                  <FormItem className="min-w-0">
                    <FormLabel className="font-medium">Price</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Price"
                        className="w-full rounded-3xl border-muted bg-white p-3"
                        type="number"
                        {...field}
                        {...form.register(
                          `variants.${index}.bulkPrices.${bulkPriceIndex}.price`,
                          { valueAsNumber: true },
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        ))}

        <div className="flex items-center justify-start">
          <button
            type="button"
            onClick={() => onAddBulkPrice(index)}
            className="rounded-md border border-primary/30 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/5"
          >
            + Add Bulk Price
          </button>
        </div>
      </FieldGroup>

      <FormField
        control={form.control}
        name={`variants.${index}.featuredImage`}
        render={({ field }) => (
          <FormItem className="min-w-0 lg:col-span-2">
            <FormLabel className="font-medium">Featured Image</FormLabel>
            <FormControl>
              <Dropzone
                label=""
                className="w-full"
                accept={{ "image/*": [] }}
                maxFiles={1}
                value={field.value ? [field.value] : []}
                onFilesChange={(files) =>
                  files.length > 0 ? field.onChange(files[0]) : field.onChange("")
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`variants.${index}.additionalImages`}
        render={({ field }) => (
          <FormItem className="min-w-0 lg:col-span-2">
            <FormLabel className="font-medium">Additional Images</FormLabel>
            <FormControl>
              <Dropzone
                label=""
                className="w-full"
                accept={{ "image/*": [] }}
                maxFiles={10}
                value={field.value}
                onFilesChange={field.onChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default VariantCardFields;
